import { getMetadata, ref } from "firebase/storage";
import { storage } from "senf-shared";

export interface ImageMetaData {
  width?: number;
  height?: number;
  contentType?: string;
  size?: number;
  timeCreated?: string;
  updated?: string;
  customMetadata?: {
    alt?: string;
    [key: string]: string | undefined;
  };
}

export const getImageMetadataFromUrl = async (
  imageUrl: string,
): Promise<ImageMetaData | null> => {
  try {
    // Check if it's a Firebase Storage URL
    if (!imageUrl.includes("firebasestorage.googleapis.com")) {
      return null;
    }

    // Extract the path from the URL
    const url = new URL(imageUrl);
    // Remove the bucket name and 'o/' prefix from the path
    const path = decodeURIComponent(url.pathname.split("/o/")[1] || "");

    if (!path) {
      console.warn(
        "Could not extract path from Firebase Storage URL:",
        imageUrl,
      );
      return null;
    }

    // Get the storage reference
    const storageRef = ref(storage, path);

    try {
      // Get the metadata
      const metadata = await getMetadata(storageRef);

      return {
        width: metadata.customMetadata?.width
          ? Number.parseInt(metadata.customMetadata.width)
          : undefined,
        height: metadata.customMetadata?.height
          ? Number.parseInt(metadata.customMetadata.height)
          : undefined,
        contentType: metadata.contentType,
        size: metadata.size,
        timeCreated: metadata.timeCreated,
        updated: metadata.updated,
        customMetadata: metadata.customMetadata,
      };
    } catch (error: any) {
      // If the object doesn't exist, return null instead of throwing
      if (error?.code === "storage/object-not-found") {
        console.warn("Image not found in Firebase Storage:", path);
        return null;
      }
      throw error; // Re-throw other errors
    }
  } catch (error) {
    console.error("Error getting image metadata:", error);
    return null;
  }
};

export const extractMetadataFromImage = async (
  imageUrl: string,
): Promise<ImageMetaData | null> => {
  try {
    // Create a new image element
    const img = new Image();

    // Create a promise to handle the image load
    const loadPromise = new Promise<ImageMetaData>((resolve, reject) => {
      img.onload = () => {
        resolve({
          width: img.naturalWidth,
          height: img.naturalHeight,
          contentType: img.src.startsWith("data:")
            ? img.src.split(";")[0].split(":")[1]
            : undefined,
        });
      };
      img.onerror = () => reject(new Error("Failed to load image"));
    });

    // Set the image source
    img.src = imageUrl;

    // Wait for the image to load
    const metadata = await loadPromise;

    // If it's a Firebase Storage URL, try to get additional metadata
    if (imageUrl.includes("firebasestorage.googleapis.com")) {
      const storageMetadata = await getImageMetadataFromUrl(imageUrl);
      // console.log("storageMetadata", storageMetadata);
      return { ...metadata, ...storageMetadata };
    }

    return metadata;
  } catch (error) {
    console.error("Error extracting image metadata:", error);
    return null;
  }
};
